import { Paper, Tab, Tabs } from "@mui/material";
import { useQuery } from "react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import qs from "qs";
import { Layout, Popin, TabPanel } from "$components";
import JImportModal from "$components/JImportModal/JImportModal";
import { useCommonStyles } from "$containers/CommonStyles/CommonStyles";
import { theme } from "$utils/theme/bataillog";
import useAuth from "$hooks/useAuth";
import useJStyles from "$hooks/useJStyles";
import { useListAdapter } from "$hooks/useList/useListAdapter";
import useOrder from "$hooks/useOrder";
import useTabs from "$hooks/useTabs";
import { appState } from "$reducers";
import {
  CompanyServices,
  EntrepotService,
  OrderService,
  DockyardServices,
} from "$services";
import { CompanyType, OrderStatus } from "$types";
import { ImportType } from "$types/import/ImportType";
import { tabs } from "$utils/constants";
import { queryClient } from "$utils/queryClient";
import { Services } from "$utils/typeConstants";
import OrderForm from "./forms/OrderForm";
import OrdersTab from "./OrdersTab";
import { downloadFile } from "$components/Export/utils";
import { CacheInvalidationAction } from "$utils/cache";

export interface IOrderListProps {
  className?: string;
}

function OrdersList() {
  const { token, isContributor, user, isClient, isAdmin } = useAuth();
  const { filter: sidebarCompanyFilter } = useSelector(appState);
  const { jclasses } = useJStyles();
  const { currentTab, handleChangeTab } = useTabs();
  const [warehouses, setWarehouses] = useState([]);
  const [companiesClient, setCompaniesClient] = useState([]);
  const [isLoadingWarehouse, setisLoadingWarehouse] = useState(false);
  const [isLoadingClientCompanies, setisLoadingClientCompanies] =
    useState(false);
  const [isLoadingSupplierCompanies, setisLoadingSupplierCompanies] =
    useState(false);
  // CLIENT OWN WAREHOUSES AND LOGICTICIEN LINKED
  const [clientWarehouses, setClientWarehouses] = useState([]);
  const [isLoadingContributorCompanies, setIsLoadingContributorCompanies] =
    useState(false);
  const commonClasses = useCommonStyles();
  const { togglePopin, getNewOrderType, defaultOptions, openSnackbar } =
    useOrder();

  // @tech @improve
  // As a Contributor... We get all warehouses of this contributor
  // To get all warehouses linked to a company we need to query by "parentCompany"
  const companyId = isContributor()
    ? user.userInfo.userCompany._id
    : sidebarCompanyFilter?._id;
  const {
    data: myContributorWarehouses,
    isLoading: isLoadingMyContributorWarehouses,
  } = useQuery(
    [
      CacheInvalidationAction.GET_ENTREPOTS,
      isContributor(),
      { company: companyId },
    ],
    () => {
      // We use this only if we have the contributor role
      if (isContributor() && !isAdmin()) {
        return EntrepotService.getEntrepots({
          parentCompany: companyId,
          rowsPerPage: 100000,
        });
      }
      return [];
    },
    { cacheTime: 5 * 1000 * 60 }
  );

  // console.log("contributorCompanies", contributorCompanies);

  const defaultFilterOrders = useMemo(() => {
    const nextProviderType =
      currentTab === tabs.TRANSFER_TAB ? "transfer" : ["provider", "reverse"];

    if (isContributor() && !isAdmin()) {
      // @tech @improve
      // We add the warehouses filter with all 'our' warehouses to see linked orders
      const warehouseIds = myContributorWarehouses?.data?.map(
        ({ _id }) => _id
      ) ?? ["impossibleId"];
      const contributorCompanyIds = [
        ...(user?.userInfo?.userCompany?.contributorOf || []),
        sidebarCompanyFilter?._id,
      ];

      if (isClient()) {
        // CLIENT ROLE !
        return {
          providerType: {
            $in: Array.isArray(nextProviderType)
              ? nextProviderType
              : [nextProviderType],
          },
          $or: [
            {
              company: { $eq: sidebarCompanyFilter?._id },
            },
            {
              warehouse: { $in: warehouseIds },
              company: { $in: contributorCompanyIds },
            },
          ],
        };
      }

      // ONLY CONTRIBUTOR : we want to see all order with OUR warehouses ...
      const warehouseIdsWithClientWarehouses = [
        ...new Set([...warehouseIds, ...clientWarehouses.map((w) => w._id)]),
      ];

      return {
        providerType: {
          $in: Array.isArray(nextProviderType)
            ? nextProviderType
            : [nextProviderType],
        },
        warehouse: { $in: warehouseIdsWithClientWarehouses },
        company: { $in: contributorCompanyIds },
      };
    }

    // Not contributor : go back to the "default" behaviour : Use the company filter (if filled)
    return {
      providerType: {
        $in: Array.isArray(nextProviderType)
          ? nextProviderType
          : [nextProviderType],
      },
      company: { $eq: sidebarCompanyFilter?._id },
      warehouse: undefined,
    };
  }, [
    currentTab,
    isContributor,
    isAdmin,
    sidebarCompanyFilter?._id,
    isClient,
    myContributorWarehouses?.data,
    clientWarehouses,
    user?.userInfo?.userCompany?.contributorOf,
  ]);

  // Only wait for data that is actually used to build the orders query filter.
  // Display fetches (warehouses, companies, dockyards) must not toggle `enabled`:
  // false → true forces a refetch even when React Query already has cache.
  const needsContributorWarehouseFilter = isContributor() && !isAdmin();
  const {
    items,
    filters,
    isFetching,
    setFilter,
    setPageIndex,
    pageIndex,
    pageCount,
    fetchItems: fetchOrderItems,
  } = useListAdapter(
    "api/order/orders/consolidated",
    {
      defaultSort: defaultOptions.defaultSort,
      populate: defaultOptions.populate,
      defaultPageSize: defaultOptions.defaultPageSize || 20,
      defaultFilters: defaultFilterOrders,
      cachePrefix: "orderslist",
      ignoreSideBarFilter: true,
      additionalParams: {},
      enabled:
        !needsContributorWarehouseFilter || !isLoadingMyContributorWarehouses,
      ...(token ? { jwt: token } : {}),
    },
    Services.GATEWAY_API
  );

  const orderItems = items as any;
  const ordersArray = useMemo(() => {
    if (Array.isArray(orderItems)) return orderItems;
    if (orderItems?.data?.length > 0) return orderItems.data;
    return [];
  }, [orderItems]);

  const warehouseIdsKey = useMemo(() => {
    const ids = [
      ...new Set(
        ordersArray
          .map((order: any) => [order.warehouse, order.warehouseOrigin])
          .flat()
          .filter(Boolean)
      ),
    ].sort();
    return ids.join(",");
  }, [ordersArray]);

  const dockyardIdsKey = useMemo(() => {
    const ids = new Set<string>();
    ordersArray.forEach((order: any) => {
      order?.suborders?.forEach((suborder: any) => {
        if (suborder?.dockyard) {
          ids.add(suborder.dockyard);
        }
      });
    });
    return Array.from(ids).sort().join(",");
  }, [ordersArray]);

  const isActuallyFetching =
    isFetching ||
    isLoadingMyContributorWarehouses ||
    isLoadingWarehouse ||
    isLoadingClientCompanies ||
    isLoadingSupplierCompanies ||
    isLoadingContributorCompanies;

  useEffect(() => {
    // Update providerType filter based on the current tab
    if (currentTab === tabs.TRANSFER_TAB) {
      setFilter("providerType", "transfer");
    } else {
      setFilter("providerType", ["provider", "reverse"]);
    }

    // Reset global filter
    setFilter("global", undefined);
  }, [currentTab, setFilter]);

  // Update
  const updateOrderItem = useCallback(async (orderId: string, data: any) => {
    await OrderService.updateOrderItem(orderId, data);
  }, []);

  // Create
  const saveOrderItem = useCallback(async (data: any) => {
    await OrderService.createOrderItem(data);
  }, []);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportUpdateModal, setShowImportUpdateModal] = useState(false);
  const [showImportUpdateAllModal, setShowImportUpdateAllModal] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRowsOrder, setSelectedRows] = useState<any[]>([]);
  const [popinOpen, setPopinOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [companiesSupplier, setCompaniesSupplier] = useState([]);
  const [dockyardsMap, setDockyardsMap] = useState<Map<string, any>>(new Map());

  // get warehouses — keyed by stable ID list, not orderItems reference
  const fetchWarehouses = useCallback(async () => {
    if (!warehouseIdsKey) {
      return [];
    }

    const listWarehouseId = warehouseIdsKey.split(",");
    const filtersInId = {
      $inId: listWarehouseId,
    };
    const queryKey = ["getEntrepots", filtersInId];
    const cached: any = queryClient.getQueryData(queryKey);
    if (cached) {
      setWarehouses(cached?.data || []);
      return cached?.data || [];
    }

    setisLoadingWarehouse(true);
    try {
      const getWarehouses: any = await queryClient.fetchQuery(
        queryKey,
        () => EntrepotService.getEntrepots(filtersInId),
        { cacheTime: 5 * 60 * 1000 }
      );
      queryClient.setQueryData(queryKey, getWarehouses);
      setWarehouses(getWarehouses?.data || []);
      return getWarehouses?.data || [];
    } finally {
      setisLoadingWarehouse(false);
    }
  }, [warehouseIdsKey]);

  // Fetch dockyards — keyed by stable ID list, not orderItems reference
  const fetchDockyards = useCallback(async () => {
    if (!dockyardIdsKey) {
      setDockyardsMap(new Map());
      return new Map();
    }

    const dockyardIds = dockyardIdsKey.split(",");
    const filtersInId = {
      $inId: dockyardIds,
    };
    const queryKey = [
      CacheInvalidationAction.GET_DOCKYARDS,
      "orders",
      dockyardIdsKey,
    ];
    const cached: any = queryClient.getQueryData(queryKey);

    const toMap = (dockyardsResponse: any) => {
      const map = new Map<string, any>();
      const dockyards = dockyardsResponse?.data || dockyardsResponse || [];
      if (Array.isArray(dockyards)) {
        dockyards.forEach((dockyard: any) => {
          if (dockyard) {
            const dockyardId = dockyard._id || dockyard.id;
            if (dockyardId) {
              map.set(dockyardId, dockyard);
            }
          }
        });
      }
      return map;
    };

    if (cached) {
      const map = toMap(cached);
      setDockyardsMap(map);
      return map;
    }

    const dockyardsResponse: any = await queryClient.fetchQuery(
      queryKey,
      () => DockyardServices.getDockyards(filtersInId),
      {
        cacheTime: 5 * 60 * 1000,
        staleTime: 5 * 60 * 1000,
      }
    );
    queryClient.setQueryData(queryKey, dockyardsResponse);
    const map = toMap(dockyardsResponse);
    setDockyardsMap(map);
    return map;
  }, [dockyardIdsKey]);

  // Query Companies
  const fetchClientCompanies = useCallback(async () => {
    const filtersCC = {
      isActive: true,
      companyTypes: CompanyType.CLIENT,
      page: 1,
      rowsPerPage: 50000,
    };
    const queryKeysCC = [
      CacheInvalidationAction.GET_COMPANIES,
      "clientCompanies",
      filtersCC,
    ];
    const cached: any = queryClient.getQueryData(queryKeysCC);
    if (cached) {
      setCompaniesClient(cached);
      return;
    }

    setisLoadingClientCompanies(true);
    try {
      let companies: any = await queryClient.fetchQuery(
        queryKeysCC,
        () => CompanyServices.getCompanies(filtersCC),
        { staleTime: 60 * 1000 }
      );
      companies = companies.data;
      await queryClient.setQueryData(queryKeysCC, companies);
      setCompaniesClient(companies);
    } finally {
      setisLoadingClientCompanies(false);
    }
  }, [setCompaniesClient]);

  // Query Supplier Companies
  const fetchSupplierCompanies = useCallback(async () => {
    const filtersSC = {
      companyTypes: CompanyType.SUPPLIER,
    };
    const queryKeysSC = [
      CacheInvalidationAction.GET_COMPANIES,
      "supplierCompanies",
      filtersSC,
    ];
    const cached: any = queryClient.getQueryData(queryKeysSC);
    if (cached) {
      setCompaniesSupplier(cached);
      return;
    }

    setisLoadingSupplierCompanies(true);
    try {
      let companies: any = await queryClient.fetchQuery(
        queryKeysSC,
        () => CompanyServices.getCompanies(filtersSC),
        { staleTime: 60 * 1000 }
      );
      companies = companies.data;
      await queryClient.setQueryData(queryKeysSC, companies);
      setCompaniesSupplier(companies);
    } finally {
      setisLoadingSupplierCompanies(false);
    }
  }, [setCompaniesSupplier]);

  // Query Contributor Companies
  const fetchContributorCompanies = useCallback(async () => {
    const contributorCompanyIds = [
      ...(user?.userInfo?.userCompany?.contributorOf || []),
      sidebarCompanyFilter?._id,
    ].filter(Boolean); // Remove any undefined/null values

    if (contributorCompanyIds.length === 0) {
      setClientWarehouses([]);
      setIsLoadingContributorCompanies(false);
      return;
    }

    const filtersCC = {
      $inId: contributorCompanyIds,
    };
    const queryKeysCC = [
      CacheInvalidationAction.GET_COMPANIES,
      "contributorCompanies",
      filtersCC,
    ];
    setIsLoadingContributorCompanies(true);
    try {
      let companies: any = await queryClient.getQueryData(queryKeysCC);
      if (!companies) {
        companies = await queryClient.fetchQuery(
          queryKeysCC,
          () => CompanyServices.getCompanies(filtersCC),
          { staleTime: 60 * 1000 }
        );
        companies = companies.data;
        await queryClient.setQueryData(queryKeysCC, companies);
      }

      const filters = {
        page: 1,
        rowsPerPage: 50000,
        ignoreContributorOf: true,
      };
      let companiesTest;
      companiesTest = await queryClient.getQueryData([
        CacheInvalidationAction.GET_COMPANIES,
        filters,
      ]);
      if (!companiesTest) {
        companiesTest = await queryClient.fetchQuery(
          [CacheInvalidationAction.GET_COMPANIES, filters],
          () => CompanyServices.getCompanies(filters),
          { staleTime: 60 * 1000 }
        );
        companiesTest = companiesTest.data;
        await queryClient.setQueryData(
          [CacheInvalidationAction.GET_COMPANIES, filters],
          companiesTest
        );
      }
      // console.log("companiesTest", companiesTest);
      // Fetch warehouses for each contributor company
      const warehousesCompanyClientArray = [];
      const clientOwnWarehouses = [];
      const clientLogisticianWarehouses = [];
      await Promise.all(
        companies.map(async (companyOfContributor) => {
          const queryKeysCCWarehouses = [
            CacheInvalidationAction.GET_ENTREPOTS,
            "warehousesCompanyClient",
            { company: companyOfContributor._id },
          ];
          let warehousesCompanyClient: any = await queryClient.getQueryData(
            queryKeysCCWarehouses
          );
          if (!warehousesCompanyClient) {
            warehousesCompanyClient = await queryClient.fetchQuery(
              queryKeysCCWarehouses,
              () =>
                EntrepotService.getEntrepots({
                  company: companyOfContributor._id,
                }),
              { staleTime: 60 * 1000 }
            );
            await queryClient.setQueryData(
              queryKeysCCWarehouses,
              warehousesCompanyClient
            );
          }

          warehousesCompanyClientArray.push(...warehousesCompanyClient.data);
          clientOwnWarehouses.push(
            ...(warehousesCompanyClient.data || []).filter(
              (e) => e.parentCompany === companyOfContributor._id
            )
          );

          clientLogisticianWarehouses.push(
            ...(warehousesCompanyClient.data || []).filter(
              (e) =>
                e.companies.some(
                  (company) => companyOfContributor._id === company._id
                ) &&
                companiesTest
                  ?.find((c) => c._id === e.parentCompany)
                  ?.companyTypes?.includes(CompanyType.LOGISTICIAN)
            )
          );
        })
      );
      // console.log("warehousesCompanyClientArray", warehousesCompanyClientArray);

      // Get unique warehouses by _id
      const uniqueClientOwnWarehouses = clientOwnWarehouses.filter(
        (warehouse, index, self) =>
          index === self.findIndex((w) => w._id === warehouse._id)
      );
      const uniqueClientLogisticianWarehouses =
        clientLogisticianWarehouses.filter(
          (warehouse, index, self) =>
            index === self.findIndex((w) => w._id === warehouse._id)
        );

      setClientWarehouses([
        ...uniqueClientOwnWarehouses,
        ...uniqueClientLogisticianWarehouses,
      ]);
    } finally {
      setIsLoadingContributorCompanies(false);
    }
  }, [user?.userInfo?.userCompany?.contributorOf, sidebarCompanyFilter?._id]);

  useEffect(() => {
    fetchClientCompanies();
    fetchSupplierCompanies();
  }, [fetchClientCompanies, fetchSupplierCompanies]);

  useEffect(() => {
    fetchContributorCompanies();
  }, [fetchContributorCompanies]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  useEffect(() => {
    fetchDockyards();
  }, [fetchDockyards]);

  // Re-fetch order when changing tab
  useEffect(() => {
    setPageIndex(0);
    // fetchAllOrders(); // @fixme : weird artifact when I merged staging to preprod
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  const handleCancelOrder = useCallback(
    async (data: any) => {
      try {
        const order = { ...data, status: OrderStatus.CANCELED };
        await updateOrderItem(order.id, order);
        setPopinOpen(false);
        openSnackbar(
          `La commande ${order.reference} a bien été annulée.`,
          5000
        );
        await fetchOrderItems();
      } catch (error) {
        console.error("Error canceling order:", error);
      }
    },
    [updateOrderItem, fetchOrderItems, openSnackbar]
  );

  const onSubmit = useCallback(
    async (data: any) => {
      try {
        setIsSubmitting(true);
        const order = { ...data };

        if (order.id) {
          await updateOrderItem(order.id, order);
          setPopinOpen(false);
        } else {
          delete order.id;
          await saveOrderItem(order);
          setPopinOpen(false);
        }
        await fetchOrderItems();
      } catch (error) {
        console.error("Error submitting order:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [updateOrderItem, saveOrderItem, fetchOrderItems]
  );

  /**
   * Build Prisma format query string from filters
   */
  const buildPrismaQuery = (
    selectedIds: number[] = [],
    pageSize = 100000
  ): string => {
    const query: Record<string, any> = {
      page: 1,
      rowsPerPage: pageSize,
    };

    // If specific orders are selected, filter by IDs
    if (selectedIds.length > 0) {
      query.id = selectedIds.join(",");
    } else {
      // Otherwise, use current filters
      // Transform filters to Prisma format
      if (filters.providerType) {
        const { providerType } = filters;
        if (
          typeof providerType === "object" &&
          providerType !== null &&
          "$in" in providerType &&
          Array.isArray(providerType.$in)
        ) {
          query.providerType = providerType.$in.join(",");
        } else if (typeof providerType === "string") {
          query.providerType = providerType;
        }
      }
      if (filters.status) {
        const { status } = filters;
        if (
          typeof status === "object" &&
          status !== null &&
          "$in" in status &&
          Array.isArray(status.$in)
        ) {
          query.status = status.$in.join(",");
        } else if (typeof status === "string") {
          query.status = status;
        }
      }
      if (filters.company) {
        const { company } = filters;
        if (
          typeof company === "object" &&
          company !== null &&
          "$eq" in company
        ) {
          query.company = company.$eq;
        } else if (
          typeof company === "object" &&
          company !== null &&
          "$in" in company &&
          Array.isArray(company.$in)
        ) {
          query.company = company.$in.join(",");
        } else if (typeof company === "string") {
          query.company = company;
        }
      }
      if (filters.warehouse) {
        const { warehouse } = filters;
        if (
          typeof warehouse === "object" &&
          warehouse !== null &&
          "$in" in warehouse &&
          Array.isArray(warehouse.$in)
        ) {
          query.warehouse = warehouse.$in.join(",");
        } else if (typeof warehouse === "string") {
          query.warehouse = warehouse;
        }
      }
      if (filters.reference) {
        query.reference = filters.reference;
      }
      // Handle type filter (Type appro: Stock, Chantier, Projet, Multiple)
      if (filters.type !== undefined && filters.type !== null) {
        if (Array.isArray(filters.type)) {
          query.suborderType = "multiple";
        } else if (typeof filters.type === "string") {
          query.suborderType = filters.type;
        }
      }
      // Handle $or conditions (for contributor/client logic)
      if (filters.$or && Array.isArray(filters.$or)) {
        // For $or with company, use the first company found
        const companyFromOr = filters.$or.find((or: any) => or.company?.$eq);
        if (companyFromOr?.company?.$eq) {
          query.company = companyFromOr.company.$eq;
        }
      }
    }

    // Add sort
    if (defaultOptions.defaultSort) {
      const sortValue =
        typeof defaultOptions.defaultSort === "string"
          ? defaultOptions.defaultSort
          : `${defaultOptions.defaultSort.field}:${defaultOptions.defaultSort.direction}`;
      query.sort = sortValue;
    }

    return qs.stringify(query);
  };

  const exportSelectedOrders = async (all: boolean, toUpdate = false) => {
    try {
      setIsExporting(true);

      const getOrderStatus = (o: any) => o?.status ?? o?.attributes?.status;
      const getReceptionsArray = (o: any): any[] => {
        const receptions = o?.receptions ?? o?.attributes?.receptions;
        if (Array.isArray(receptions)) return receptions;
        if (Array.isArray(receptions?.data)) return receptions.data;
        return [];
      };

      // When exporting an "update" file, the gateway intentionally excludes:
      // - orders that already have receptions
      // - canceled orders
      // We pre-filter locally to avoid downloading an empty file without explanation.
      const eligibleSelectedRows =
        !all && toUpdate
          ? selectedRowsOrder.filter((o) => {
              const receptionsArr = getReceptionsArray(o);
              const hasReceptions = receptionsArr.length > 0;
              const status = getOrderStatus(o);
              const isCanceled =
                status === OrderStatus.CANCELED || status === "canceled";
              return !hasReceptions && !isCanceled;
            })
          : selectedRowsOrder;

      const selectedOrdersIds = all
        ? []
        : eligibleSelectedRows.map((o) => o.id).filter(Boolean);

      if (!all && toUpdate) {
        const removedCount =
          selectedRowsOrder.length - eligibleSelectedRows.length;
        if (removedCount > 0) {
          openSnackbar(
            `${removedCount} commande(s) ignorée(s) car déjà réceptionnée(s) ou annulée(s).`,
            5000
          );
        }
        if (selectedOrdersIds.length === 0) {
          openSnackbar(
            "Aucune commande exportable pour la mise à jour (déjà réceptionnée(s) ou annulée(s)).",
            5000
          );
          return;
        }
      }
      // Build Prisma format query
      const prismaQuery = buildPrismaQuery(selectedOrdersIds, 100000);

      const result = await OrderService.exportOrders(
        token,
        prismaQuery,
        toUpdate
      );
      await downloadFile(result, "Orders", "xlsx", true);
    } catch (error) {
      console.error("Error exporting orders:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const onImportFinish = async () => {
    await fetchOrderItems();
  };

  const handlePageChange = useCallback(
    (newPageIndex) => {
      setPageIndex(newPageIndex);
      // Force a data fetch after page change
      setTimeout(() => {
        fetchOrderItems();
      }, 0);
    },
    [setPageIndex, fetchOrderItems]
  );

  return (
    <div className={jclasses.jWrapper}>
      <Layout
        title="Liste des commandes"
        contentClassName={jclasses.jLayoutCustom}
      >
        <Paper className={jclasses.jPaper}>
          <div>
            <Tabs
              className={commonClasses.tabHeader}
              TabIndicatorProps={{
                style: { background: theme.color.blueLight, height: 3 },
              }}
              value={currentTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={handleChangeTab}
              aria-label="shipments tabs"
            >
              <Tab label="Fournisseurs/reverse" />
              <Tab label="Transfert" />
            </Tabs>
          </div>
          <TabPanel value={currentTab} index={0}>
            <OrdersTab
              setSelectedOrder={setSelectedOrder}
              selectedRowsOrder={selectedRowsOrder}
              setSelectedRows={setSelectedRows}
              setShowImportModal={setShowImportModal}
              setShowImportUpdateModal={setShowImportUpdateModal}
              setShowImportUpdateAllModal={setShowImportUpdateAllModal}
              setPopinOpen={setPopinOpen}
              isTransfer={false}
              filteredOrders={
                Array.isArray(orderItems) ? orderItems : orderItems?.data || []
              }
              warehouses={warehouses}
              exportSelectedOrders={exportSelectedOrders}
              companiesSupplier={companiesSupplier}
              companiesClient={companiesClient}
              isFetching={isActuallyFetching}
              setFilter={setFilter}
              setPageIndex={handlePageChange}
              pageIndex={pageIndex}
              pageCount={pageCount > 0 ? pageCount : 2}
              filters={filters}
              togglePopin={togglePopin}
              getNewOrderType={getNewOrderType}
              isExporting={isExporting}
              dockyardsMap={dockyardsMap}
            />
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            <OrdersTab
              setSelectedOrder={setSelectedOrder}
              selectedRowsOrder={selectedRowsOrder}
              setSelectedRows={setSelectedRows}
              setShowImportModal={setShowImportModal}
              setShowImportUpdateModal={setShowImportUpdateModal}
              setShowImportUpdateAllModal={setShowImportUpdateAllModal}
              setPopinOpen={setPopinOpen}
              isTransfer
              filteredOrders={
                Array.isArray(orderItems) ? orderItems : orderItems?.data || []
              }
              warehouses={warehouses}
              exportSelectedOrders={exportSelectedOrders}
              companiesSupplier={companiesSupplier}
              companiesClient={companiesClient}
              isFetching={isActuallyFetching}
              setFilter={setFilter}
              setPageIndex={handlePageChange}
              pageIndex={pageIndex}
              pageCount={pageCount > 0 ? pageCount : 2}
              filters={filters}
              togglePopin={togglePopin}
              getNewOrderType={getNewOrderType}
              isExporting={isExporting}
              dockyardsMap={dockyardsMap}
            />
          </TabPanel>
        </Paper>
        <Popin
          className={commonClasses.ProjectsPage_Popin}
          openPopin={popinOpen}
          toggleDrawer={setPopinOpen}
          minWidth="70%"
        >
          <OrderForm
            order={selectedOrder}
            onSubmit={onSubmit}
            onCancelOrder={handleCancelOrder}
            onReset={() => setPopinOpen(false)}
            setPopinOpen={setPopinOpen}
            isSubmitting={isSubmitting}
            isTransfer={currentTab === tabs.TRANSFER_TAB}
          />
        </Popin>
        {/* @TODO @improve : mutualiser ces 3 JImportModal */}
        <JImportModal
          dataName="commandes"
          showImportModal={showImportModal}
          setShowImportModal={setShowImportModal}
          openSnackbar={openSnackbar}
          type={ImportType.ORDER}
          onImportFinish={onImportFinish}
        />
        <JImportModal
          dataName="commandes"
          canExportData
          showImportModal={showImportUpdateModal}
          setShowImportModal={setShowImportUpdateModal}
          openSnackbar={openSnackbar}
          type={ImportType.ORDER_UPDATE}
          onImportFinish={onImportFinish}
          customDownload={() => exportSelectedOrders(false, true)}
          isExportingOrdersList={isExporting}
          pastParticiple="modifié"
          isFeminine
        />
        <JImportModal
          dataName="commandes"
          canExportData
          showImportModal={showImportUpdateAllModal}
          setShowImportModal={setShowImportUpdateAllModal}
          openSnackbar={openSnackbar}
          type={ImportType.ORDER_UPDATE}
          onImportFinish={onImportFinish}
          customDownload={() => exportSelectedOrders(true, true)}
          isExportingOrdersList={isExporting}
          pastParticiple="modifié"
          isFeminine
        />
      </Layout>
    </div>
  );
}

export default OrdersList;
